import { useState } from "react";
import { motion } from "framer-motion";
import { PackageSearch, Check, Truck, Warehouse, CreditCard } from "lucide-react";
import { api } from "@/lib/api";
import { formatUSD } from "@/lib/format";
import { Footer } from "@/components/Footer";
import { BackButton } from "@/components/BackButton";

const STEPS = [
  { key: "paid", label: "Payment confirmed", icon: CreditCard },
  { key: "sent_to_cj", label: "Sent to our fulfilment partner", icon: Warehouse },
  { key: "shipped", label: "On its way", icon: Truck },
];

export default function TrackOrder() {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const r = await api.get("/orders/track", { params: { order_id: orderId, email } });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "We couldn't find that order. Check the number and email.");
    } finally {
      setLoading(false);
    }
  };

  const stepIndex = (r) => {
    if (!r) return -1;
    if (r.payment_status !== "paid") return -1;
    if (r.tracking?.number || r.tracking?.status) return 2;
    if (r.fulfillment === "sent_to_cj") return 1;
    return 0;
  };

  const inputCls =
    "w-full rounded-md border border-charcoal/20 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal";

  return (
    <main data-testid="track-order-page" className="pt-24">
      <div className="mx-auto max-w-[900px] px-6 py-24 sm:px-10">
        <BackButton />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="mb-4 text-xs uppercase tracking-[0.3em] text-taupe">Track my order</p>
          <h1 className="font-serif text-4xl font-light tracking-tighter sm:text-5xl">Where is my order?</h1>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-ash">
            Enter your order number (it starts with SONY-) and the email you used at checkout.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input data-testid="track-order-id" placeholder="Order number — e.g. SONY-AB12CD34" value={orderId}
              onChange={(e) => setOrderId(e.target.value)} className={inputCls} />
            <input data-testid="track-email" type="email" placeholder="Email used at checkout" value={email}
              onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          </div>
          <button
            data-testid="track-submit"
            onClick={submit}
            disabled={loading || !orderId || !email}
            className="mt-6 flex items-center gap-3 rounded-full bg-charcoal px-8 py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-moss disabled:opacity-60"
          >
            <PackageSearch size={15} /> {loading ? "Looking it up..." : "Track order"}
          </button>
          {error && <p data-testid="track-error" className="mt-6 text-sm text-moss">{error}</p>}
        </motion.div>

        {result && (
          <motion.div data-testid="track-result" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }} className="mt-16 rounded-md border border-charcoal/10 bg-sand/40 p-8 sm:p-12">
            <div className="flex flex-wrap items-baseline justify-between gap-4">
              <h2 className="font-serif text-2xl tracking-tight">Order {result.order_id}</h2>
              <span className="text-sm text-ash">{formatUSD(result.amount)}</span>
            </div>
            <div className="mt-10 space-y-0">
              {STEPS.map((s, i) => {
                const active = i <= stepIndex(result);
                const Icon = s.icon;
                return (
                  <div key={s.key} data-testid={`track-step-${s.key}`} className="flex items-center gap-5 py-4">
                    <span className={`flex h-10 w-10 items-center justify-center rounded-full border ${active ? "border-moss bg-moss text-bone" : "border-charcoal/20 text-ash"}`}>
                      {active ? <Check size={16} /> : <Icon size={16} />}
                    </span>
                    <p className={`text-sm ${active ? "text-charcoal" : "text-ash"}`}>{s.label}</p>
                  </div>
                );
              })}
            </div>
            {result.payment_status !== "paid" && (
              <p className="mt-6 text-sm font-light text-ash">Payment hasn't been confirmed for this order yet.</p>
            )}
            {result.tracking?.number && (
              <p data-testid="tracking-number" className="mt-6 text-sm text-charcoal">
                Tracking: <span className="font-medium">{result.tracking.number}</span>
                {result.tracking.carrier ? ` via ${result.tracking.carrier}` : ""}
              </p>
            )}
            <div className="mt-10 border-t border-charcoal/10 pt-8">
              <p className="mb-4 text-xs uppercase tracking-[0.2em] text-ash">In this order</p>
              <ul className="space-y-2 text-sm font-light">
                {result.items.map((it, i) => (
                  <li key={i} className="flex justify-between gap-4">
                    <span>{it.name}{it.variant ? ` — ${it.variant}` : ""} × {it.qty}</span>
                    <span className="text-ash">{formatUSD(it.price * it.qty)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </div>
      <Footer />
    </main>
  );
}
